import React from 'react';
import { ArrowLeft, FileText, CheckCircle2 } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { appPath } from '../utils/appRouting';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: 'Accettazione dei Termini',
      content: `Accedendo e utilizzando la piattaforma SOSTEGNO PRO+ ("la Piattaforma"), l'utente accetta di essere vincolato 
        dai presenti Termini e Condizioni. Se non si accettano questi termini, non si deve utilizzare la Piattaforma.`
    },
    {
      title: 'Descrizione del Servizio',
      content: `SOSTEGNO PRO+ è una piattaforma digitale progettata per supportare le istituzioni scolastiche nella gestione 
        dei Piani Educativi Individualizzati (PEI) e dei documenti relativi agli studenti con bisogni educativi speciali, 
        in conformità con il D.Lgs. 66/2017 e successive modifiche.`
    },
    {
      title: 'Requisiti di Accesso',
      content: `L'accesso alla Piattaforma è riservato al personale autorizzato delle istituzioni scolastiche che hanno 
        sottoscritto un contratto di licenza. Ogni utente deve essere maggiorenne e dotato di credenziali di accesso 
        personali e non trasferibili.`
    },
    {
      title: 'Obblighi dell\'Utente',
      list: [
        'Mantenere la riservatezza delle proprie credenziali di accesso',
        'Non condividere l\'account con terze parti',
        'Utilizzare la Piattaforma esclusivamente per le finalità previste',
        'Non tentare di accedere a dati o funzionalità non autorizzate',
        'Segnalare immediatamente qualsiasi violazione della sicurezza',
        'Rispettare la normativa sulla protezione dei dati personali'
      ]
    },
    {
      title: 'Uso dell\'Intelligenza Artificiale (AI) e Fair Use Policy',
      content:
        `Alcune funzionalità della Piattaforma utilizzano servizi di Intelligenza Artificiale per generare contenuti e suggerimenti.
        Anche nei piani che includono "crediti AI illimitati", l'uso è soggetto a una Fair Use Policy finalizzata a prevenire abusi e a
        garantire un accesso equo e stabile a tutti gli utenti.\n\n` +
        `La Fair Use Policy può includere, a titolo esemplificativo: limitazioni di frequenza delle richieste, rallentamenti (throttling),
        riduzione temporanea di priorità, blocchi temporanei in caso di uso anomalo o automatizzato, e/o ulteriori verifiche di sicurezza.\n\n` +
        `È vietato utilizzare l'AI in modo da compromettere il servizio o aggirare le misure di protezione (es. invio massivo/automatizzato
        di richieste, scraping, condivisione di account per moltiplicare il traffico, tentativi di elusione di limiti tecnici).\n\n` +
        `In caso di violazioni o sospetto abuso, SOSTEGNO PRO+ può applicare restrizioni, sospendere temporaneamente l'accesso alle funzionalità
        AI o al servizio, e/o richiedere chiarimenti prima di ripristinare l'operatività.`
    },
    {
      title: 'Protezione dei Dati',
      content: `La Piattaforma tratta dati personali, inclusi dati sensibili relativi alla salute degli studenti, in conformità 
        con il Regolamento UE 2016/679 (GDPR) e la normativa italiana sulla privacy. Per maggiori informazioni, consultare 
        la nostra Privacy Policy.`
    },
    {
      title: 'Proprietà Intellettuale',
      content: `Tutti i contenuti della Piattaforma, inclusi software, grafiche, loghi, testi e documentazione, sono di 
        proprietà esclusiva di SOSTEGNO PRO+ o dei suoi licenzianti e sono protetti dalle leggi sul diritto d'autore 
        e sulla proprietà intellettuale.`
    },
    {
      title: 'Limitazione di Responsabilità',
      content: `SOSTEGNO PRO+ fornisce la Piattaforma "così com'è" e non garantisce che il servizio sia privo di errori o 
        interruzioni. In nessun caso SOSTEGNO PRO+ sarà responsabile per danni indiretti, incidentali o consequenziali 
        derivanti dall'uso della Piattaforma.`
    },
    {
      title: 'Disponibilità del Servizio',
      content: `Ci impegniamo a garantire la massima disponibilità della Piattaforma. Tuttavia, ci riserviamo il diritto di 
        sospendere temporaneamente il servizio per manutenzione programmata o in caso di emergenze tecniche, 
        previa comunicazione quando possibile.`
    },
    {
      title: 'Modifiche ai Termini',
      content: `SOSTEGNO PRO+ si riserva il diritto di modificare i presenti Termini e Condizioni in qualsiasi momento. 
        Le modifiche entreranno in vigore dalla data di pubblicazione sulla Piattaforma. L'uso continuato della 
        Piattaforma dopo le modifiche costituisce accettazione dei nuovi termini.`
    },
    {
      title: 'Risoluzione',
      content: `SOSTEGNO PRO+ può sospendere o terminare l'accesso alla Piattaforma in caso di violazione dei presenti 
        Termini e Condizioni, senza preavviso e senza pregiudizio di eventuali azioni legali.`
    },
    {
      title: 'Legge Applicabile',
      content: `I presenti Termini e Condizioni sono regolati dalla legge italiana. Per qualsiasi controversia relativa 
        all'interpretazione o all'esecuzione dei presenti termini sarà competente il Foro di Roma.`
    }
  ];

  return (
    <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-muted/30 to-background text-foreground scroll-smooth">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(appPath('/'))}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Torna alla Home</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                <span className="text-yellow-400 font-bold text-sm">S+</span>
              </div>
              <span className="text-sm font-semibold text-foreground hidden sm:block">SOSTEGNO PRO+</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Title Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-2xl mb-4">
            <FileText className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Termini e Condizioni d'Uso</h1>
          <p className="text-muted-foreground">Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}</p>
        </div>

        {/* Content */}
        <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
          <div className="p-6 md:p-8 space-y-8">
            {sections.map((section, index) => (
              <section key={index}>
                <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 text-sm font-bold">
                    {index + 1}
                  </span>
                  {section.title}
                </h2>
                {section.content && (
                  <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                )}
                {section.list && (
                  <ul className="space-y-2 mt-2">
                    {section.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 text-sm font-bold">
                  12
                </span>
                Contatti
              </h2>
              <div className="bg-muted/40 rounded-xl p-5 border border-border">
                <p className="text-muted-foreground text-sm mb-3">
                  Per domande sui presenti Termini e Condizioni, contattaci:
                </p>
                <p className="text-muted-foreground">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:info@sostegnopro.it" className="text-blue-600 hover:underline">
                    info@sostegnopro.it
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <Link to={appPath('/privacy')} className="hover:text-blue-600 transition-colors">Privacy Policy</Link>
          <Link to={appPath('/note-legali-ai')} className="hover:text-blue-600 transition-colors">Note legali AI</Link>
          <Link to={appPath('/cookie-policy')} className="hover:text-blue-600 transition-colors">Cookie Policy</Link>
          <Link to={appPath('/')} className="hover:text-blue-600 transition-colors">Home</Link>
        </div>
      </main>
    </div>
  );
};

export default TermsAndConditions;
